import { useState, useEffect, useMemo, useCallback, memo } from 'react'
import EditorWrapper from '../../GraphEditor/EditorWrapper'
import { resolveGraphOpen } from '@/utils/resolveGraphOpen'
import { Link } from 'react-router-dom'
import type { DocumentRecord } from '@/types/catalog'
import { apiService } from '@/service/api'
import InterviewPreview from './InterviewPreview'
import UserArtifactPreview from '@/components/UserArtifact/UserArtifactPreview'
import VideoBriefPreview from '@/components/VideoBriefPreview'
import DatasheetPreview from '@/components/DatasheetPreview'
import TimeseriesPreview from '@/components/TimeseriesPreview'
import LiveStreamPreview from '@/components/LiveStreamPreview'
import DocumentFilePreview from './PdfPreview'
import { cleanDocumentName } from '../utils/stripGraphPrefix'
import { Trash2, ExternalLink, Loader2 } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { useRenameDocument } from '@/hooks/useRenameDocument'
import { documentLoadQueue } from '@/utils/documentLoadQueue'
import { useMaybeEditor, TLShapeId, useValue } from 'tldraw'
import { useAppStore } from '@/store'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'
import { timeseriesViewerPath } from '@/lib/timeseriesViewer'

interface DocumentPreviewProps {
  documentId: string | undefined
  name: string | undefined
  width?: number
  height?: number
  shapeId?: string
  showFooter?: boolean
  onRemoveFromBoard?: () => void
  /**
   * A tag to land on, as this file spells it: a sheet highlights and scrolls to
   * its column, a PDF or a drawing opens already searching for it. Tag
   * Mapping's Source file sets it; a board card never does.
   */
  highlightTag?: string
  /**
   * A P&ID graph's node to land on: the editor selects it and draws its box,
   * as `?highlightNode=` does on the graph-editor route. Tag Mapping's
   * drill-down sets it; a board card never does.
   */
  highlightNodeId?: string | null
  /**
   * Open a P&ID / PFD drawing as its parsed GRAPH, in the editor, the way a
   * board P&ID card shows it (Original | Detected | Review). A board card IS
   * the graph document already; Tag Mapping holds the drawing's own id, so it
   * opts in here and the graph is resolved from the drawing's properties.
   */
  openDrawingAsGraph?: boolean
  /**
   * A fixed size for the file-name title. On the board it scales with the
   * canvas zoom (11px at 100%, larger zoomed out); off the board there is no
   * zoom, so Tag Mapping sets a steady size instead.
   */
  titleFontSizePx?: number
}

// Cache entry with timestamp for TTL-based expiration
interface CacheEntry {
  document: DocumentRecord
  cachedAt: number
}

const CACHE_TTL_MS = 60_000 // 60 seconds

// Global cache for document metadata to prevent redundant API calls
const documentCache = new Map<string, CacheEntry>()

// Global in-flight request tracker to prevent duplicate fetches
const inFlightRequests = new Map<string, Promise<DocumentRecord>>()

function getCachedDocument(key: string): DocumentRecord | undefined {
  const entry = documentCache.get(key)
  if (!entry) return undefined
  if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
    documentCache.delete(key)
    return undefined
  }
  return entry.document
}

function setCachedDocument(key: string, document: DocumentRecord): void {
  documentCache.set(key, { document, cachedAt: Date.now() })
}
/** Pre-populate the cache from an already-fetched document list (e.g. boardSlice.getDocuments).
 *  Eliminates the N+1 individual GET /document/:id calls on first board load. */
export function primeDocumentCache(documents: DocumentRecord[]): void {
  for (const doc of documents) {
    // Skip entries already in cache (still within TTL) — they may have been
    // populated by a richer individual GET /document/:id fetch.
    if (!getCachedDocument(doc.id)) {
      setCachedDocument(doc.id, doc)
    }
  }
}

/** Flush the entire document cache — called on board navigation to keep the
 *  module-level cache in sync with the Zustand board lifecycle. */
export function clearDocumentCache(): void {
  documentCache.clear()
  inFlightRequests.clear()
}

/** Invalidate a specific document from the board preview cache */
export function invalidateDocumentCache(documentId: string): void {
  documentCache.delete(documentId)
}

const DocumentPreview = ({
  documentId,
  name,
  width,
  height,
  shapeId,
  showFooter: showFooterProp,
  onRemoveFromBoard,
  highlightTag,
  highlightNodeId,
  openDrawingAsGraph = false,
  titleFontSizePx,
}: DocumentPreviewProps) => {
  const { t } = useTranslation('board')
  // Null off the board: Tag Mapping mounts this same preview with no canvas
  // behind it, so there is no shape to select and no zoom to counter.
  const editor = useMaybeEditor()
  // Track if this shape is selected to conditionally show footer buttons
  const isSelected = useValue(
    'is selected',
    () => shapeId && editor ? editor.getSelectedShapeIds().includes(shapeId as TLShapeId) : false,
    [editor, shapeId]
  )
  // Track zoom level to counter-scale footer buttons so they stay legible
  const zoom = useValue('zoom', () => editor?.getZoomLevel() ?? 1, [editor])
  const [document, setDocument] = useState<DocumentRecord | null>(null)
  const [pnidDocument, setPnidDocument] = useState<DocumentRecord | null>(null)
  const [loading, setLoading] = useState(true)
  // Optimistic rename overlay — takes precedence over the `name` prop / fetched
  // document name so the header repaints immediately on rename.
  const [nameOverride, setNameOverride] = useState<string | null>(null)
  // Stable callback for removing from board - only recreated when shapeId/editor changes
  const handleRemoveFromBoard = useCallback(() => {
    if (shapeId && editor) {
      editor.deleteShape(shapeId as TLShapeId)
    }
    if (onRemoveFromBoard) {
      onRemoveFromBoard()
    }
  }, [shapeId, editor, onRemoveFromBoard])
  const containerClasses =
    'h-full flex flex-col bg-white dark:bg-dark-card'

  useEffect(() => {
    const fetchDocument = async () => {
      if (!documentId) return

      // Check cache first
      const cacheKey = `${documentId}` // documentId is already globally unique
      const cachedDoc = getCachedDocument(cacheKey)

      if (cachedDoc) {
        setDocument(cachedDoc)
        setLoading(false)
        return
      }

      // Check if there's already an in-flight request for this document
      let inFlightPromise = inFlightRequests.get(cacheKey)

      if (!inFlightPromise) {
        // Create new in-flight request
        inFlightPromise = documentLoadQueue.add(cacheKey, async () => {
          // Double-check cache in case another request completed while we were queued
          const existingDoc = getCachedDocument(cacheKey)
          if (existingDoc) return existingDoc

          const fetchedDoc = await apiService.getDocument(documentId)
          // Cache the document metadata
          setCachedDocument(cacheKey, fetchedDoc)
          return fetchedDoc
        })

        // Track the in-flight request
        inFlightRequests.set(cacheKey, inFlightPromise)

        // Clean up in-flight request when complete (success or failure)
        inFlightPromise.finally(() => {
          inFlightRequests.delete(cacheKey)
        })
      }

      setLoading(true)
      try {
        const doc = await inFlightPromise
        setDocument(doc)
      } catch (err) {
        console.error('Failed to load document:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchDocument()
  }, [documentId])

  // Fetch PNID document for graph documents to get the original filename
  useEffect(() => {
    const fetchPnidDocument = async () => {
      if (!document || document.type !== 'graph' || !document.properties.pnid) {
        setPnidDocument(null)
        return
      }

      // Check cache first
      const cacheKey = document.properties.pnid
      const cachedDoc = getCachedDocument(cacheKey)

      if (cachedDoc) {
        setPnidDocument(cachedDoc)
        return
      }

      // Check if there's already an in-flight request for this document
      let inFlightPromise = inFlightRequests.get(cacheKey)

      if (!inFlightPromise) {
        // Create new in-flight request
        inFlightPromise = documentLoadQueue.add(cacheKey, async () => {
          // Double-check cache in case another request completed while we were queued
          const existingDoc = getCachedDocument(cacheKey)
          if (existingDoc) return existingDoc

          const fetchedDoc = await apiService.getDocument(document.properties.pnid!)
          // Cache the document metadata
          setCachedDocument(cacheKey, fetchedDoc)
          return fetchedDoc
        })

        // Track the in-flight request
        inFlightRequests.set(cacheKey, inFlightPromise)

        // Clean up in-flight request when complete (success or failure)
        inFlightPromise.finally(() => {
          inFlightRequests.delete(cacheKey)
        })
      }

      try {
        const pnidDoc = await inFlightPromise
        setPnidDocument(pnidDoc)
      } catch (err) {
        console.error('Failed to load PNID document:', err)
      }
    }

    fetchPnidDocument()
  }, [document])

  const previewContent = useMemo(() => {
    if (!documentId || loading) return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
    if (!document) return <div className="p-4">{t('document_preview.document_not_found')}</div>

    // 'excel' documents (xlsx/xls/csv uploads) carry a runtime type that isn't
    // part of the DocumentRecord.type literal union; treat them like datasheets.
    if ((document.type as string) === 'excel') {
      return <DatasheetPreview docId={documentId} highlightColumn={highlightTag} />
    }

    switch (document.type) {
      case 'graph':
        return (
          <EditorWrapper
            key={documentId}
            graphId={documentId}
            initialZoom={1}
            isReadOnly
            allowEntityDeletion
            highlightNodeId={highlightNodeId}
          />
        )
      case 'interview':
        return <InterviewPreview docId={documentId} />
      case 'document': {
        // xlsx/xls/csv files uploaded as 'document' should use the datasheet viewer
        const ext = document.name?.split('.').pop()?.toLowerCase()
        if (['xlsx', 'xls', 'csv'].includes(ext || '') && document.properties?.db_path) {
          return <DatasheetPreview docId={documentId} highlightColumn={highlightTag} />
        }
        return <DocumentFilePreview docId={documentId} width={width} height={height} initialSearch={highlightTag} />
      }
      case 'pnid':
      case 'pfd': {
        // The same resolver the catalog opens a drawing with: first page's
        // graph for a multi-page PDF. Not parsed yet → the file itself.
        const drawingGraphId = openDrawingAsGraph ? resolveGraphOpen(document.properties).graphId : undefined
        if (drawingGraphId) {
          return (
            <EditorWrapper
              key={drawingGraphId}
              graphId={drawingGraphId}
              initialZoom={1}
              isReadOnly
              allowEntityDeletion
              highlightNodeId={highlightNodeId}
              initialSearch={highlightNodeId ? undefined : highlightTag}
            />
          )
        }
        return <DocumentFilePreview docId={documentId} width={width} height={height} initialSearch={highlightTag} />
      }
      case 'timeseries':
        // Show DatasheetPreview if db_path exists (xlsx/csv files)
        // Show TimeseriesPreview (JSON viewer) for JSON timeseries files
        if (document.properties?.db_path) {
          return <DatasheetPreview docId={documentId} highlightColumn={highlightTag} />
        }
        return <TimeseriesPreview docId={documentId} />
      // A live stream has no file, no db_path and no copied values, so it gets
      // its own viewer rather than a branch inside the file-shaped ones.
      case 'live_stream':
        return <LiveStreamPreview docId={documentId} />
      case 'datasheet':
        return <DatasheetPreview docId={documentId} highlightColumn={highlightTag} />
      // FS-557 "User Artifact" — a call transcript whose body is markdown.
      case 'transcript':
        return <UserArtifactPreview docId={documentId} />
      // A rendered "Explain as video" brief: the video itself; the full page adds the slide list.
      case 'video':
        return <VideoBriefPreview docId={documentId} status={document.status} />
      // No viewer exists for a device config, in the catalogue either. Say so
      // rather than rendering nothing: UI-03 was reported as the card being
      // "completely broken", which is what an empty preview looks like.
      case 'device_config':
      default:
        return (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-muted">
            {t('document_preview.preview_not_available', {
              defaultValue: 'Document preview not available',
            })}
          </div>
        )
    }
  }, [document, documentId, loading, width, height, highlightTag, highlightNodeId, openDrawingAsGraph, t])

  const getEditLink = useCallback(() => {
    const dataCatalogUrl = '/data-catalog'

    if (!document) return dataCatalogUrl

    // Read board name fresh from store (not from stale closure)
    const storeState = useAppStore.getState()
    const currentBoardName = storeState.board?.name || 'Board'
    const params = new URLSearchParams()
    // Always set boardName when on a board page (board object exists in store).
    // Off a board (no editor) the store can still hold the last board visited,
    // which would title the destination with a board the reader did not come from.
    if (editor && storeState.board) params.set('boardName', currentBoardName)
    if (document.name) params.set('docName', document.name)
    const boardParam = params.toString() ? `?${params.toString()}` : ''

    // 'excel' documents share the datasheet route (see previewContent above).
    if ((document.type as string) === 'excel') {
      return `/datasheet/${documentId}${boardParam}`
    }

    switch (document.type) {
      case 'live_stream':
        return `/live-stream/${documentId}${boardParam}`
      case 'graph':
        return `/graph-editor/${documentId}${boardParam}`
      case 'interview':
        return `/interview/${documentId}${boardParam}`
      case 'document': {
        const ext = document.name?.split('.').pop()?.toLowerCase()
        if (['xlsx', 'xls', 'csv'].includes(ext || '') && document.properties?.db_path) {
          return `/datasheet/${documentId}${boardParam}`
        }
        return `/document/${documentId}${boardParam}`
      }
      case 'pnid':
      case 'pfd':
        return `/document/${documentId}${boardParam}`
      case 'timeseries':
        // datasheet for xlsx/csv with a db_path; otherwise the timeseries viewer
        return (timeseriesViewerPath({ ...document, id: String(documentId) }) ?? `/timeseries/${documentId}`) + boardParam
      case 'datasheet':
        return `/datasheet/${documentId}${boardParam}`
      // The same route the catalogue opens a User Artifact with
      // (DataCatalogV2 `case DOC_TYPES.transcript`).
      case 'transcript':
        return `/user-artifact/${documentId}${boardParam}`
      case 'video':
        return `/video/${documentId}${boardParam}`
      default:
        return dataCatalogUrl
    }
  }, [document, documentId, editor])

  // Board-safe event handler that prevents event propagation to tldraw canvas
  const createBoardSafeHandler = (handler: () => void) => {
    return (e: React.MouseEvent | React.PointerEvent) => {
      e.stopPropagation()
      e.preventDefault()
      handler()
    }
  }

  // Determine which name to display - prefer PNID filename for graph documents
  const displayName = useMemo(() => {
    // An optimistic rename wins over every other source.
    if (nameOverride !== null) {
      return cleanDocumentName(nameOverride, document?.type) || 'Untitled'
    }
    if (pnidDocument) {
      // Strip via the *source's* type so a pnid/pfd source whose name is
      // "P&ID Foo.pdf" cleans up; and the legacy graph-prefix in case it
      // somehow rode along. Fall back to 'Untitled' when the strip yields
      // an empty string (name was *exactly* the prefix) — falling back
      // to pnidDocument.name would re-inject the very marker we removed.
      return cleanDocumentName(pnidDocument.name, pnidDocument.type) || 'Untitled'
    }
    // Prefer pre-resolved name prop (from docNameMap) over document.name
    // to avoid flicker when document.name shows graph name before pnidDocument loads
    const raw = name || document?.name
    if (!raw) return ''
    // Same fallback rationale as above — never re-inject the bare prefix.
    return cleanDocumentName(raw, document?.type) || 'Untitled'
  }, [document, pnidDocument, name, nameOverride])

  // Rename the underlying document. P&ID/PFD cards display the SOURCE document's
  // name (graph.properties.pnid), so renaming a graph card must target that
  // source — mirrors the on-canvas card handler in DocumentPreviewShape/index.
  const renameDocument = useRenameDocument()
  const handleRename = useCallback(
    (newName: string) => {
      if (!documentId) return
      const targetId =
        (document?.type === 'graph' ? document?.properties?.pnid : documentId) || documentId
      const previous = displayName
      void renameDocument(targetId, newName, {
        previousName: previous,
        // The board keys this card's overlay by its own documentId (the graph
        // doc for P&ID/PFD cards), while the API + documents store use the
        // renamed source doc (targetId).
        overlayKey: documentId,
        applyName: (n) => {
          setNameOverride(n)
          invalidateDocumentCache(targetId)
        },
      })
    },
    [renameDocument, documentId, document, displayName],
  )

  // Use type-specific label for the "Open" button
  const isDiagram = document?.type === 'pnid' || document?.type === 'pfd' || document?.type === 'graph'
  const isTimeseries = document?.type === 'timeseries'
  const viewButtonLabel = isDiagram
    ? t('document_preview.view_diagram')
    : isTimeseries
      ? t('document_preview.view_timeseries')
      : t('document_preview.view_document')

  // FS-500: no click-to-center camera pan here — this component only renders
  // inside the preview modal now (the on-canvas card is `DocumentIcon`), and
  // panning the background while the user reads the preview is the bug.

  return (
    <div
      className={containerClasses}
      style={{
        // CSS containment isolates this component's layout, style, and paint calculations
        // from the rest of the page, improving rendering performance during board operations
        contain: 'layout style paint',
        // GPU acceleration hints for smooth drag performance
        willChange: 'transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    >
      {/* HEADER - Drag handle area (pointerEvents: none = passes to tldraw for drag) */}
      <header
        className="flex justify-between px-4 py-2 items-center border-b border-gray-200 dark:border-dark-border cursor-grab active:cursor-grabbing shrink-0"
        style={{ pointerEvents: 'all' }}
      >
        <div
          className="min-w-0 flex-1"
          style={{ fontSize: `${titleFontSizePx ?? Math.max(6, 11 / Math.max(zoom, 0.4))}px` }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <FileNameWithCopy
            name={displayName}
            className="!text-[1em] !font-medium"
            iconClassName="w-3 h-3"
            onRename={handleRename}
            stopPropagation
          />
        </div>
        {shapeId && !showFooterProp && <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">{t('document_preview.drag_hint', 'Drag here to move')}</span>}
      </header>
      {/* CONTENT - Interactive area for PDF/document */}
      {/* Stop propagation to prevent TLDraw from dragging the card when interacting with content */}
      <div
        className={clsx('relative flex-1 min-h-0')}
        style={{ pointerEvents: 'all' }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {previewContent}
      </div>
      {/* FOOTER - Interactive buttons, visible when shape is selected or showFooter is true */}
      {(showFooterProp || isSelected) && isFeatureAllowedInEmbed('documentActions') && (
        <footer
          className="flex justify-between items-center border-t border-gray-200 dark:border-dark-border px-4 py-1.5 shrink-0"
          style={{
            pointerEvents: 'all',
            ...(!showFooterProp ? { zoom: 1 / Math.max(zoom, 0.5) } : {}),
          }}
        >
          {(shapeId || onRemoveFromBoard) && (
            <button
              onClick={createBoardSafeHandler(handleRemoveFromBoard)}
              onPointerDown={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border bg-destructive/5 dark:bg-destructive/15 text-destructive border-destructive/30 hover:bg-destructive/10 dark:hover:bg-destructive/25"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('document_preview.remove_from_board')}
            </button>
          )}
          <Link
            to={getEditLink()}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border bg-brand/5 dark:bg-brand/15 text-brand dark:text-brand-light border-brand/30 hover:bg-brand/10 dark:hover:bg-brand/25 ml-auto"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {viewButtonLabel}
          </Link>
        </footer>
      )}
    </div>
  )
}

export default memo(DocumentPreview)
